import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { Cell, Sector } from 'recharts'
import { ChartContainer } from '../../../../../common/chart/chartContainer'
import Bar from '../../../../../common/chart/chartContainer/Bar'
import { Legend } from '../../../../../common/chart/legend'
import { PieChartContainer } from '../../../../../common/chart/pieChart'
import { Element } from '../../../../../common/html/element'
import { Notification } from '../../../../../common/notification'
import { SCREEN } from '../../../../../common/selectCurrencyUnit/constant'
import { useFormatCurrency } from '../../../../../common/selectCurrencyUnit/useFormatCurrency'
import { Tabs } from '../../../../../common/tabs'
import { getAppendStyleItemWithCustom } from '../../../../../common/tabs/helper'
import EventEmitter, {
  DID_MOUNT_COMPONENT,
} from '../../../../../helpers/EventEmitter'
import { getUnique } from '../../../../../helpers/common'
import { formatByTheme } from '../../../../../helpers/theme'
import { formatVal, valToPercent } from '../../../../../helpers/value'
import { EMPTY_VALUE } from '../../../../../utils/common'
import { getNoDataComponent } from '../../helper'
import classes from '../../index.module.scss'

const MAX_BAR_SIZE = 40
const MAX_BAR_AUTO_HEIGHT = 7

const EachYear = ({
  data,
  dataPieParent,
  dataPieChild,
  dataPieBottomLeft,
  dataPieBottomRight,
  tabs,
  onChangeTab,
  isNoData,
  valueTotal,
  year,
  isDynamicReport,
  isDownloadComponent,
  isLifeInsurance,
  isBottomLeft,
  isBottomRight,
  chart,
}) => {
  const ref = useRef()
  const pieParentChartId = useRef(getUnique())
  const pieChildChartId = useRef(getUnique())
  const pieBottomLeftChartId = useRef(getUnique())
  const pieBottomRightChartId = useRef(getUnique())

  const { formatCurrencyWithUnit } = useFormatCurrency(
    SCREEN.FINANCIAL_ANALYSIS,
  )

  const locale = useSelector((state) => state.i18n.locale)

  const [size, setSize] = useState(0)
  const [dataPieKey, setDataPieKey] = useState(dataPieParent[1].id)

  const dataPieChildren = dataPieChild[dataPieKey]

  useEffect(() => {
    window.addEventListener('resize', calcSize)
    return () => window.removeEventListener('resize', calcSize)
  }, [])

  const calcSize = () => {
    setSize(ref.current?.offsetWidth)
  }

  useEffect(() => {
    calcSize()
  }, [isNoData])

  const onClickPie = (entry) => setDataPieKey(entry.id)

  const indexActive = dataPieParent.findIndex((item) => item.id === dataPieKey)

  const CustomizedLabel = (props) => {
    const locale = useSelector((state) => state.i18n.locale)
    let { x, y, value, width, height } = props

    y += isDynamicReport || isDownloadComponent ? 9 : 0
    x += (isDynamicReport || isDownloadComponent) && value >= 80 ? width / 2 : 0

    if (value >= 80) {
      return (
        <g style={{ pointerEvents: 'none' }}>
          <foreignObject
            x={x}
            y={y}
            height={height}
            width={width}
            color="#FFFFFF"
          >
            <Element
              className="h100 w100 dFlex alignItemsCenter justifyContentCenter"
              style={{ color: '#ffffff' }}
            >
              {valToPercent(
                value,
                locale,
                true,
                undefined,
                value === 100 ? 0 : 2,
              )}
            </Element>
          </foreignObject>
        </g>
      )
    }

    const valuePercent = valToPercent(value, locale, true)

    return (
      <g>
        <foreignObject
          x={x + (valuePercent !== EMPTY_VALUE ? width : 0) + 3}
          y={y}
          height={height}
          width={200}
        >
          <Element
            className="h100 w100 dFlex alignItemsCenter"
            style={{ color: '#3A414B' }}
          >
            {valuePercent}
          </Element>
        </foreignObject>
      </g>
    )
  }

  const renderCustomActiveShape = (dataKey) => (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, id } =
      props

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        {dataKey === id && (
          <Sector
            cx={cx}
            cy={cy}
            startAngle={startAngle - 0.3}
            endAngle={endAngle + 0.3}
            innerRadius={outerRadius - 1}
            outerRadius={outerRadius + 5}
            fill={fill}
          />
        )}
      </g>
    )
  }

  const renderChartAssetStructure = () => {
    return (
      <>
        <div className="fw600">
          <Translate value="financialAnalysis.ASSET_STRUCTURE" />
        </div>
        <ChartContainer
          data={data}
          width="100%"
          height={
            data.length <= MAX_BAR_AUTO_HEIGHT || isBottomLeft || isBottomRight
              ? '100%'
              : MAX_BAR_SIZE * data.length
          }
          layout="vertical"
          margin={{ left: 8 }}
          cartesianGrid={{
            vertical: true,
            horizontal: false,
            stroke: formatByTheme('#E5E5E8'),
          }}
          yAxis={[
            {
              dataKey: 'name',
              yAxisId: 'left',
              type: 'category',
              width: 200,
              tickStyle: { color: '#3A414B' },
              lineClamp: 2,
            },
          ]}
          xAxis={[
            {
              type: 'number',
              dataKeys: ['value'],
              ticks: [0, 20, 40, 60, 80, 100],
              axisLine: false,
              hide: true,
            },
          ]}
          tooltip={{
            schema: {
              title: {
                hide: true,
              },
              content: [
                {
                  dataKey: 'value',
                  formatValue: (value) => {
                    return formatCurrencyWithUnit({
                      value: (value / 100) * valueTotal,
                      year,
                      exchangeRateType: 'LastDayRate',
                    })
                  },
                },
              ],
            },
          }}
        >
          <Bar
            yAxisId={'left'}
            dataKey={'value'}
            maxBarSize={MAX_BAR_SIZE}
            label={<CustomizedLabel />}
          >
            {data.map((item, index) => {
              return <Cell key={`cell-${index}`} fill={item.color} />
            })}
          </Bar>
        </ChartContainer>
      </>
    )
  }

  const renderChartResourceStructureDynamicReport = (props = {}) => {
    const { indexActiveCustom, dataPieChildrenCustom, dataKey } = props

    return (
      <div className="h100 flexColumn">
        <div className="h100 dFlex">
          <div className="h100 w45 px1 py1 dFlex alignItemsCenter justifyContentEnd">
            <div ref={ref} className="h100 dFlex alignItemsCenter">
              <PieChartContainer
                chartId={pieParentChartId.current}
                data={dataPieParent}
                width={190}
                height={190}
                isLabelActive={false}
                formatValueTooltip={(val) => formatVal(val, locale) + '%'}
                onPieClick={onClickPie}
                isDisableActiveIndex
                defaultActiveIndex={indexActiveCustom}
                renderCustomActiveShape={renderCustomActiveShape(dataKey)}
              />
            </div>
            <div className="h100 dFlex alignItemsCenter pl3 pr5">
              <div>
                <Legend
                  items={dataPieParent.map((item, index) => ({
                    ...item,
                    title: item.name,
                    pieIndex: index,
                    isI18n: false,
                  }))}
                  className="flexColumn"
                />
              </div>
            </div>
          </div>
          <div className="h100 w10 dFlex alignItemsCenter justifyContentCenter fw600">
            <Translate value="financialAnalysis.IN_WHICH" />
          </div>
          <div className="h100 w45 px1 py1 dFlex alignItemsCenter">
            <div className="h100 dFlex alignItemsCenter pl5 pr3">
              <PieChartContainer
                chartId={pieChildChartId.current}
                data={dataPieChildrenCustom}
                width={170}
                height={170}
                isLabelActive={false}
                formatValueTooltip={(val) => formatVal(val, locale) + '%'}
                propsTooltip={{
                  allowEscapeViewBox: { x: false, y: false },
                  styleLabel: {
                    maxWidth: 200,
                    whiteSpace: 'pre-wrap',
                    paddingRight: 20,
                  },
                }}
              />
            </div>
            <div className="h100 dFlex alignItemsCenter">
              <div>
                <Legend
                  chartId={pieChildChartId.current}
                  items={dataPieChildrenCustom.map((item, index) => ({
                    ...item,
                    title: item.name,
                    pieIndex: index,
                    isI18n: false,
                  }))}
                  className="flexColumn"
                />
              </div>
            </div>
          </div>
        </div>
        {isLifeInsurance && (
          <div className="h100 dFlex">
            {isBottomLeft && (
              <>
                <div className="h100 w45 px1 py1 dFlex alignItemsCenter justifyContentEnd">
                  <div className="h100 dFlex alignItemsCenter">
                    <PieChartContainer
                      chartId={pieBottomLeftChartId.current}
                      data={dataPieBottomLeft}
                      width={190}
                      height={190}
                      isLabelActive={false}
                      formatValueTooltip={(val) => formatVal(val, locale) + '%'}
                      propsTooltip={{
                        allowEscapeViewBox: { x: false, y: false },
                        styleLabel: {
                          maxWidth: 200,
                          whiteSpace: 'pre-wrap',
                          paddingRight: 20,
                        },
                      }}
                    />
                  </div>
                  <div className="h100 dFlex alignItemsCenter pl3 pr5">
                    <div>
                      <Legend
                        chartId={pieBottomLeftChartId.current}
                        items={dataPieBottomLeft.map((item, index) => ({
                          ...item,
                          title: item.name,
                          pieIndex: index,
                          isI18n: false,
                        }))}
                        className="flexColumn"
                      />
                    </div>
                  </div>
                </div>
                <div className="h100 w10 dFlex alignItemsCenter justifyContentCenter fw600">
                  {/* Space */}
                </div>
              </>
            )}
            {isBottomRight && (
              <div className="h100 w45 px1 py1 dFlex alignItemsCenter">
                <div className="h100 dFlex alignItemsCenter pl5 pr3">
                  <PieChartContainer
                    chartId={pieBottomRightChartId.current}
                    data={dataPieBottomRight}
                    width={170}
                    height={170}
                    isLabelActive={false}
                    formatValueTooltip={(val) => formatVal(val, locale) + '%'}
                    propsTooltip={{
                      allowEscapeViewBox: { x: false, y: false },
                      styleLabel: {
                        maxWidth: 200,
                        whiteSpace: 'pre-wrap',
                        paddingRight: 20,
                      },
                    }}
                  />
                </div>
                <div className="h100 dFlex alignItemsCenter">
                  <div>
                    <Legend
                      chartId={pieBottomRightChartId.current}
                      items={dataPieBottomRight.map((item, index) => ({
                        ...item,
                        title: item.name,
                        pieIndex: index,
                        isI18n: false,
                      }))}
                      className="flexColumn"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  useEffect(() => {
    if (isDynamicReport && !chart.includes(1)) {
      EventEmitter.dispatch(DID_MOUNT_COMPONENT)
    }
  }, [])

  if (isDynamicReport) {
    const getPropResourceStructure = (index) => ({
      indexActiveCustom: index,
      dataPieChildrenCustom: dataPieChild[dataPieParent[index].id],
      dataKey: dataPieParent[index].id,
    })

    return (
      <div>
        <div className={`dFlex justifyContentEnd ${classes.tabContainer}`}>
          {!isNoData && (
            <div className="fw600 py1 w100">
              <Translate value="common.FISCAL_YEAR" />: {year}
            </div>
          )}
        </div>
        {isNoData ? (
          getNoDataComponent(true)
        ) : (
          <>
            {chart.includes(1) && (
              <div style={{ height: 350 }} className="flexColumn mt2">
                {renderChartAssetStructure()}
              </div>
            )}
            {chart.includes(2) && (
              <div style={{ height: 260 }} className="flexColumn mt2">
                <div className="fw600 pb2">
                  <Translate value="financialAnalysis.LIABILITIES_STRUCTURE" />
                </div>
                {renderChartResourceStructureDynamicReport(
                  getPropResourceStructure(0),
                )}
              </div>
            )}
            {chart.includes(3) && (
              <div style={{ height: 260 }} className="flexColumn mt2">
                <div className="fw600 pb2">
                  <Translate value="financialAnalysis.EQUITY_STRUCTURE" />
                </div>
                {renderChartResourceStructureDynamicReport(
                  getPropResourceStructure(1),
                )}
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  return (
    <div className="flexColumn h100">
      <Element
        style={{
          borderBottom: !isDynamicReport ? '1px solid #B5CEF8' : undefined,
        }}
        className={`dFlex justifyContentEnd ${classes.tabContainer}`}
      >
        <Tabs
          activeKey={year}
          items={tabs}
          tabListClassName={classes.tabs}
          getAppendStyleItem={getAppendStyleItemWithCustom()}
          onChange={onChangeTab}
        />
      </Element>
      {isNoData ? (
        <div className="h100 pt2 pb2 dFlex alignItemCenter justifyContentCenter">
          <Notification isI18n value="common.NO_DATA" />
        </div>
      ) : (
        <div className="h100 pt1 dFlex">
          <div className="flexColumn w50 h100 pr1">
            {renderChartAssetStructure()}
          </div>
          <div className="w50 h100 pl1 flexColumn">
            <div className="fw600">
              <Translate value="financialAnalysis.RESOURCE_STRUCTURE" />
            </div>
            <div className="h100 flexColumn">
              <div className="h100 dFlex">
                <div
                  ref={ref}
                  className="h100 w40 px1 py1 dFlex alignItemsCenter justifyContentCenter"
                >
                  <PieChartContainer
                    chartId={pieParentChartId.current}
                    data={dataPieParent}
                    width={size}
                    height={190}
                    isLabelActive={false}
                    formatValueTooltip={(val) => formatVal(val, locale) + '%'}
                    onPieClick={onClickPie}
                    isDisableActiveIndex
                    defaultActiveIndex={indexActive}
                    renderCustomActiveShape={renderCustomActiveShape(
                      dataPieKey,
                    )}
                  />
                </div>
                <div className="h100 w20 dFlex alignItemsCenter justifyContentCenter fw600">
                  <Translate value="financialAnalysis.IN_WHICH" />
                </div>
                <div className="h100 w40 px1 py1 dFlex alignItemsCenter justifyContentCenter">
                  <PieChartContainer
                    chartId={pieChildChartId.current}
                    data={dataPieChildren}
                    width={size}
                    height={170}
                    isLabelActive={false}
                    formatValueTooltip={(val) => formatVal(val, locale) + '%'}
                    propsTooltip={{
                      allowEscapeViewBox: { x: false, y: false },
                      styleLabel: {
                        maxWidth: 200,
                        whiteSpace: 'pre-wrap',
                        paddingRight: 20,
                      },
                    }}
                  />
                </div>
              </div>
              <div className="dFlex w100">
                <div className="w40 dFlex justifyContentCenter">
                  <div>
                    <Legend
                      items={dataPieParent.map((item, index) => ({
                        ...item,
                        title: item.name,
                        pieIndex: index,
                        isI18n: false,
                      }))}
                      className="flexColumn"
                    />
                  </div>
                </div>
                <div className="w20 h100" />
                <div className="w40 dFlex justifyContentCenter">
                  <div>
                    <Legend
                      chartId={pieChildChartId.current}
                      items={dataPieChildren.map((item, index) => ({
                        ...item,
                        title: item.name,
                        pieIndex: index,
                        isI18n: false,
                      }))}
                      className="flexColumn"
                    />
                  </div>
                </div>
              </div>
            </div>
            {isLifeInsurance && (isBottomLeft || isBottomRight) && (
              <div className="h100 flexColumn">
                <div className="h100 dFlex">
                  {isBottomLeft && (
                    <>
                      <div className="h100 w40 px1 py1 dFlex alignItemsCenter justifyContentCenter">
                        <PieChartContainer
                          chartId={pieBottomLeftChartId.current}
                          data={dataPieBottomLeft}
                          width={size}
                          height={190}
                          isLabelActive={false}
                          formatValueTooltip={(val) =>
                            formatVal(val, locale) + '%'
                          }
                          propsTooltip={{
                            allowEscapeViewBox: { x: false, y: false },
                            styleLabel: {
                              maxWidth: 200,
                              whiteSpace: 'pre-wrap',
                              paddingRight: 20,
                            },
                          }}
                        />
                      </div>
                      <div className="h100 w20 dFlex alignItemsCenter justifyContentCenter fw600">
                        {/* Space */}
                      </div>
                    </>
                  )}
                  {isBottomRight && (
                    <div className="h100 w40 px1 py1 dFlex alignItemsCenter justifyContentCenter">
                      <PieChartContainer
                        chartId={pieBottomRightChartId.current}
                        data={dataPieBottomRight}
                        width={size}
                        height={170}
                        isLabelActive={false}
                        formatValueTooltip={(val) =>
                          formatVal(val, locale) + '%'
                        }
                        propsTooltip={{
                          allowEscapeViewBox: { x: false, y: false },
                          styleLabel: {
                            maxWidth: 200,
                            whiteSpace: 'pre-wrap',
                            paddingRight: 20,
                          },
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="dFlex w100">
                  {isBottomLeft && (
                    <>
                      <div className="w40 dFlex justifyContentCenter">
                        <div>
                          <Legend
                            chartId={pieBottomLeftChartId.current}
                            items={dataPieBottomLeft.map((item, index) => ({
                              ...item,
                              title: item.name,
                              pieIndex: index,
                              isI18n: false,
                            }))}
                            className="flexColumn"
                          />
                        </div>
                      </div>
                      <div className="w20 h100" />
                    </>
                  )}
                  {isBottomRight && (
                    <div className="w40 dFlex justifyContentCenter">
                      <div>
                        <Legend
                          chartId={pieBottomRightChartId.current}
                          items={dataPieBottomRight.map((item, index) => ({
                            ...item,
                            title: item.name,
                            pieIndex: index,
                            isI18n: false,
                          }))}
                          className="flexColumn"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default EachYear
